/* ==============================================================================
   Saída de áudio da chamada
   ==============================================================================
   Este componente existe por causa de dois bugs que se somavam para produzir o
   pior resultado possível: uma chamada sem som nenhum.

   BUG 1 — o áudio remoto nunca era anexado a nada
   ===============================================
   O `AudioSink` dentro do `VoiceStage` procurava a faixa de áudio percorrendo a
   lista de VÍDEOS do participante:

       for (const video of videos) {
         if (video.ownerSessionId === p.sessionId &&
             video.stream.getAudioTracks().length > 0) return video.stream;
       }
       return null;

   E a lista de vídeos era montada no `AppShell` com um filtro que EXCLUI
   exatamente o que ele procurava:

       for (const track of participant.tracks) {
         if (track.purpose === 'microphone') continue;   // <-- aqui
         videos.push(...)
       }

   O resultado é `audioStream === null` sempre, `srcObject` nunca atribuído, e
   ninguém ouvindo ninguém. A malha negociava, o ICE conectava, os bytes
   chegavam — e paravam no `RTCPeerConnection`, sem destino.

   Este componente lê `participant.tracks` DIRETO, filtrando por
   `purpose === 'microphone'` em vez de contra ele.

   BUG 2 — o som morria ao trocar de canal
   =======================================
   O `AudioSink` era filho do `VoiceStage`. Depois de corrigir a precedência de
   render (o palco não sequestra mais a área principal), abrir um canal de texto
   durante a chamada DESMONTA o palco — e com ele os elementos `<audio>`.
   Desmontar um `<audio>` para o navegador é parar a reprodução.

   Ou seja: consertar a navegação teria introduzido "a voz corta quando eu abro o
   chat". Áudio de chamada não pode ser filho de uma tela.

   Por isso o componente é montado no `AppShell`, IRMÃO do `AppLayout`, e não
   renderiza nada visível. Ele vive enquanto a chamada vive, independente do que
   está na tela.

   Por que `<audio>` e não Web Audio
   =================================
   Um `AudioContext` daria ganho por participante com precisão de dB e
   espacialização. Mas `element.volume` já resolve volume individual, e cada nó
   de Web Audio a mais é CPU competindo com os encoders de vídeo durante uma
   chamada em mesh. O `<audio>` também é o único caminho para `setSinkId`, que é
   como se escolhe o fone de saída.
   ============================================================================== */

/* ==============================================================================
   BUG 3 — no Android não saía som nenhum
   ==============================================================================
   Duas causas somadas, e nenhuma delas aparecia no computador.

   CAUSA A — `play()` recusado, e o erro engolido
   ---------------------------------------------
   A política de autoplay exige gesto do usuário. Entrar na chamada É um gesto, e
   é por isso que o áudio de voz costuma começar. Mas o elemento de áudio de
   quem começa a TRANSMITIR A TELA é criado depois, sem gesto nenhum — a pessoa
   que escuta não tocou em nada, foi a outra que apertou "compartilhar". Aí
   `play()` é recusado com `NotAllowedError`.

   O `catch` era vazio. A recusa não tinha recuperação, não tinha nova tentativa
   e não tinha aviso: só silêncio, com a interface mostrando a transmissão
   normalmente. Era o relato "não consegui escutar em lives".

   No computador a mesma recusa quase nunca acontece, porque a política é mais
   frouxa depois que a página já reproduziu áudio uma vez.

   Agora: qualquer recusa marca a reprodução como bloqueada, e a PRÓXIMA
   interação com a página — qualquer toque, em qualquer lugar — tenta de novo
   todas as faixas. Enquanto isso um aviso diz o que fazer, porque um som que
   volta sozinho depois de um toque acidental é indistinguível de defeito
   intermitente.

   CAUSA B — `display: none`
   -------------------------
   Elemento de mídia sem caixa de layout é candidato a ser descartado ou
   estrangulado por navegador de celular, que é agressivo com trabalho de fundo
   para poupar bateria. `<audio>` sem controles já não desenha nada; escondê-lo
   com `display: none` não economiza pixel algum e custa a garantia de que ele
   existe para o motor de mídia.

   Trocado por uma caixa de 1px fora da tela: o elemento está no layout, e
   continua invisível.
============================================================================== */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { VoiceEngineState } from '@concord/core';

export interface VoiceAudioProps {
  /** Estado do motor de voz. `null` quando não há chamada. */
  state: VoiceEngineState | null;
  /** Ganho por sessão, de 0 a 3. Ausente = 1 (sem alteração). */
  volumes: Record<string, number>;
  /** `deviceId` de saída, ou `null` para o padrão do sistema. */
  outputDeviceId: string | null;
  /** Quando ensurdecido, TODAS as faixas são silenciadas. */
  deafened: boolean;
  /**
   * Sessões cuja TRANSMISSÃO está sem som — o microfone da pessoa continua.
   *
   * Conjunto e não mapa de booleanos: a pergunta é de pertinência, e ausência
   * significa "com som", que é o padrão. Um mapa exigiria criar uma entrada
   * `false` para cada pessoa que nunca teve o som desligado.
   */
  screenAudioMuted?: ReadonlySet<string> | undefined;
}

export function VoiceAudio({
  state,
  volumes,
  outputDeviceId,
  deafened,
  screenAudioMuted,
}: VoiceAudioProps) {
  /*
   * Uma faixa de áudio por par, extraída de `participant.tracks`.
   *
   * `isSelf` é excluído deliberadamente: reproduzir o próprio microfone é
   * realimentação — a pessoa se ouve com o atraso do pipeline, o que torna
   * impossível falar. Quem quer testar o microfone usa o medidor nas
   * configurações, que mostra o nível sem devolver o som.
   */
  const sinks = useMemo(() => {
    if (state === null || !state.connected) return [];

    const result: Array<{
      sessionId: string;
      stream: MediaStream;
      isScreen: boolean;
      owner: string;
    }> = [];

    for (const participant of state.participants) {
      if (participant.isSelf) continue;

      for (const track of participant.tracks) {
        /*
         * `microphone` E o áudio de sistema que vem junto do compartilhamento de
         * tela. Os dois são som que precisa sair no fone.
         *
         * O áudio de tela chega com `purpose === 'screen'` e `kind === 'audio'`:
         * checar o `kind` da faixa é o que separa "a tela dele" de "o som da
         * tela dele". Sem isso, quem compartilha um vídeo compartilha imagem
         * sem áudio, e ninguém entende por quê.
         */
        const isAudio = track.track.kind === 'audio';
        if (!isAudio) continue;

        result.push({
          // A chave inclui o id da faixa: um par pode ter mic + áudio de tela.
          sessionId: `${participant.sessionId}:${track.track.id}`,
          stream: track.stream,
          /*
           * O som da TELA é distinguido do microfone.
           *
           * São a mesma pessoa e a mesma sessão, mas quem quer ver a tela sem
           * ouvir o jogo — e continuar ouvindo a voz de quem transmite — precisa
           * que os dois sejam controláveis em separado. Achatar os dois num
           * controle só transformaria "desliga o som do jogo" em "muta a
           * pessoa".
           */
          isScreen: track.purpose !== 'microphone',
          owner: participant.sessionId,
        });
      }
    }

    return result;
  }, [state]);

  /* ==========================================================================
     Reprodução bloqueada, e como ela se desbloqueia
     ==========================================================================
     `blocked` é quantas faixas foram recusadas; `token` é o que força as
     tentativas de novo.

     O token é um número que só cresce, e não um booleano de "tentar agora".
     Com booleano seria preciso voltá-lo a falso depois, e entre o toque e esse
     retorno chegaria a próxima faixa achando que já pode tocar. O número muda,
     todo efeito que depende dele roda, e não há estado para limpar.
  ========================================================================== */
  const [blocked, setBlocked] = useState(0);
  const [token, setToken] = useState(0);

  const reportBlocked = useCallback(() => setBlocked((n) => n + 1), []);

  const retry = useCallback(() => {
    setBlocked(0);
    setToken((n) => n + 1);
  }, []);

  /*
   * Qualquer toque na página serve de gesto.
   *
   * O aviso é o plano B. O plano A é isto: a pessoa vai tocar em ALGO — o botão
   * do microfone, a lista, a própria transmissão — e esse toque já é o gesto que
   * a política de autoplay exige. Esperar que ela entenda e acerte o aviso seria
   * cobrar leitura por um problema que se resolve sozinho no próximo toque.
   *
   * `pointerdown` e não `click`: chega antes, e um arraste na barra de volume
   * (que nunca vira `click`) também conta como gesto para o navegador.
   *
   * O ouvinte SÓ existe enquanto há bloqueio. Permanente, ele rodaria a cada
   * toque durante a chamada inteira para não fazer nada.
   */
  useEffect(() => {
    if (blocked === 0) return;

    const onGesture = () => retry();
    document.addEventListener('pointerdown', onGesture, { once: true });
    document.addEventListener('keydown', onGesture, { once: true });
    return () => {
      document.removeEventListener('pointerdown', onGesture);
      document.removeEventListener('keydown', onGesture);
    };
  }, [blocked, retry]);

  if (sinks.length === 0) return null;

  return (
    <>
      {/*
        Caixa de 1px fora da tela, e não `display: none`.

        Ver a nota do BUG 3 no cabeçalho: elemento de mídia sem caixa de layout é
        candidato a ser estrangulado por navegador de celular. `aria-hidden` e
        `overflow: hidden` mantêm o conjunto invisível e fora da leitura de tela.
      */}
      <div
        aria-hidden="true"
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          width: 1,
          height: 1,
          overflow: 'hidden',
          opacity: 0,
          pointerEvents: 'none',
        }}
      >
        {sinks.map((sink) => (
          <PeerAudio
            key={sink.sessionId}
            stream={sink.stream}
            /*
             * O volume é buscado por `owner` — a SESSÃO — e não pela chave
             * composta: ajustar o volume de alguém afeta o microfone e o som da
             * tela dessa pessoa juntos, porque são a mesma fonte para quem
             * escuta.
             *
             * Já DESLIGAR o som da transmissão atinge só as faixas de tela.
             * São controles diferentes de propósito: quem quer ver a tela sem
             * ouvir o jogo continua ouvindo a voz de quem transmite.
             *
             * Ensurdecer vence os dois — é o controle mais amplo, e um "estou
             * surdo" que deixasse passar o áudio de uma tela não seria surdez.
             */
            volume={
              deafened || (sink.isScreen && screenAudioMuted?.has(sink.owner) === true)
                ? 0
                : (volumes[sink.owner] ?? 1)
            }
            outputDeviceId={outputDeviceId}
            retryToken={token}
            onBlocked={reportBlocked}
          />
        ))}
      </div>

      {/* ------------------------------------------------------------------
          Aviso de som bloqueado
          ------------------------------------------------------------------
          Não é um pedido de permissão — é a explicação de um silêncio que já
          está acontecendo. Sem ele, a tela mostra a transmissão rodando e não
          sai som, e a conclusão razoável de quem vê isso é que o produto está
          quebrado.

          Ele mesmo é um botão, para quem quiser resolver de imediato; mas o
          ouvinte de gesto acima faz o toque em qualquer outro lugar funcionar
          igual.
      ------------------------------------------------------------------ */}
      {blocked > 0 && (
        <button
          type="button"
          onClick={retry}
          className="glass fixed flex items-center gap-2 px-4 py-2.5 font-semibold"
          style={{
            left: '50%',
            transform: 'translateX(-50%)',
            bottom: 'calc(var(--layout-commandbar) + env(safe-area-inset-bottom, 0px))',
            borderRadius: 'var(--radius-full)',
            boxShadow: 'var(--glass-edge), 0 8px 26px oklch(0% 0 0 / 0.5)',
            fontSize: 'var(--text-xs)',
            color: 'var(--color-text-primary)',
            zIndex: 'var(--z-dropdown)',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"
               stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"
               strokeLinejoin="round">
            <path d="M8.5 3.2L5.3 5.8H2.8v4.4h2.5l3.2 2.6V3.2z" />
            <path d="M11.2 6.2l3 3.6M14.2 6.2l-3 3.6" />
          </svg>
          Toque para ativar o som
        </button>
      )}
    </>
  );
}

/* ==============================================================================
   Um elemento por faixa
============================================================================== */

function PeerAudio({
  stream,
  volume,
  outputDeviceId,
  retryToken,
  onBlocked,
}: {
  stream: MediaStream;
  volume: number;
  outputDeviceId: string | null;
  /** Muda a cada gesto do usuário, forçando nova tentativa de `play()`. */
  retryToken: number;
  /** Chamado quando o navegador recusa a reprodução. */
  onBlocked: () => void;
}) {
  const ref = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    /*
     * `srcObject` é PROPRIEDADE do elemento, não atributo.
     *
     * React não sabe atribuí-la pelo JSX: passar `srcObject={stream}` produz uma
     * tentativa de `setAttribute` com o `MediaStream` convertido para string
     * ("[object MediaStream]"), e o elemento fica sem fonte. Só um efeito
     * resolve.
     */
    if (element.srcObject !== stream) {
      element.srcObject = stream;
    }

    /*
     * `play()` explícito, apesar do `autoPlay`.
     *
     * O `autoPlay` sozinho falha silenciosamente quando o `srcObject` é
     * atribuído DEPOIS da montagem, que é exatamente este caso.
     *
     * A recusa é TRATADA, e não engolida. Ver o BUG 3 no cabeçalho: o `catch`
     * vazio era a razão de o Android ficar mudo numa transmissão sem nada
     * indicar o motivo.
     *
     * `AbortError` é filtrado porque não é bloqueio: ele significa que uma nova
     * atribuição de `srcObject` interrompeu esta promessa, e o efeito seguinte
     * já vai chamar `play()` de novo. Contá-lo como bloqueio faria o aviso
     * "toque para ativar o som" piscar a cada troca de faixa numa chamada que
     * está tocando perfeitamente.
     */
    void element.play().catch((erro: unknown) => {
      const nome = erro instanceof Error ? erro.name : '';
      if (nome === 'AbortError') return;
      onBlocked();
    });
  }, [stream, retryToken, onBlocked]);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    /*
     * Até 100%, quem manda é `element.volume`. Acima, o `GainNode` abaixo.
     *
     * A divisão é deliberada e não é preguiça: no caminho comum — todo mundo em
     * volume normal — nenhum nó de Web Audio é criado, e o áudio segue pelo
     * caminho barato do elemento. `AudioContext` custa CPU, e numa malha de oito
     * a CPU está disputada com os codificadores de vídeo.
     *
     * Passando de 1, o elemento vai a volume cheio e o excedente vira ganho.
     */
    element.volume = Math.min(1, Math.max(0, volume));

    /*
     * `muted` além de `volume: 0`.
     *
     * Volume zero ainda decodifica o áudio; `muted` permite ao navegador
     * descartar o trabalho. Numa sala de oito, ensurdecer economiza sete
     * decodificações de Opus.
     */
    element.muted = volume === 0;
  }, [volume]);

  /* ==========================================================================
     Amplificação acima de 100%
     ==========================================================================
     `HTMLMediaElement.volume` é definido no intervalo [0, 1] pela especificação
     — atribuir 1.8 lança `IndexSizeError` em alguns navegadores e é serrado em
     1 nos outros. Não existe "volume 180%" por essa via, e era por isso que o
     controle parava em 100%: o teto não era escolha de interface, era o teto do
     elemento.

     O caminho que amplifica é Web Audio: a mesma faixa entra num
     `MediaStreamAudioSourceNode`, passa por um `GainNode` com ganho maior que 1
     e sai no destino do contexto.

     O grafo é montado SOB DEMANDA, na primeira vez que alguém passa de 100%, e
     nunca desmontado enquanto a faixa existir. Criar e destruir `AudioContext` a
     cada movimento do controle produziria estalos — e o controle se move em
     arrasto contínuo, dezenas de vezes por segundo.

     Enquanto o grafo existe o ELEMENTO é silenciado, senão o som sairia duas
     vezes: uma pelo elemento e outra pelo contexto. O elemento continua
     existindo e tocando, porque é ele que mantém a faixa fluindo e é ele que
     `setSinkId` sabe redirecionar.

     Limite de 3. Com o compressor na frente (ver a nota abaixo) 300% ainda sai
     limpo — sem ele, 200% já distorcia. O teto é o ponto em que o ruído de fundo
     da outra pessoa começa a subir junto com a voz dela, e aí amplificar mais
     deixa de melhorar a inteligibilidade.
  ========================================================================== */
  const audioGraph = useRef<{
    context: AudioContext;
    gain: GainNode;
  } | null>(null);

  /* ==========================================================================
     Por que ganho puro NÃO resolve "a voz está baixa"
     ==========================================================================
     Multiplicar por 2 multiplica TUDO por 2 — inclusive os picos, que já estavam
     perto do máximo. O que passa de 1.0 é serrado, e o resultado é uma voz que
     continua parecendo baixa nas partes calmas e distorce nas altas. É o
     comportamento clássico de "aumentei o volume e ficou pior".

     O problema real de um microfone mal configurado não é volume, é FAIXA
     DINÂMICA: a diferença entre a parte mais alta e a mais baixa da fala é
     grande demais, e o que se ouve é a média — que é baixa.

     Um compressor reduz essa diferença antes de amplificar. Ele segura só o que
     passa do limiar, e aí o ganho levanta o conjunto inteiro sem que os picos
     estourem. É por isso que a voz fica mais ALTA de verdade, e não só mais
     multiplicada.

     Os valores, e a razão de cada um:

       threshold -30 dB  Fala normal em chamada fica entre -35 e -15 dBFS. A -30
                         o compressor pega quase toda a fala e quase nenhum
                         silêncio — mais alto que isso e ele só atuaria nos
                         gritos, que não são o problema.

       ratio 6           Firme, não suave. Com 2 ou 3 a correção é sutil demais
                         para um microfone que está inaudível; acima de 8 a voz
                         perde a variação natural e fica com aquele som de rádio.

       knee 12           Transição gradual em vez de abrupta. Com knee 0 dá para
                         ouvir o compressor ligando e desligando entre sílabas.

       attack 4 ms       Rápido o bastante para pegar a consoante inicial, que é
                         o pico da fala. Mais lento deixa o "P" e o "T"
                         passarem direto e estourarem.

       release 220 ms    Perto do tempo de uma sílaba. Mais curto produz
                         "bombeamento" — o ruído de fundo sobe e desce audível
                         entre as palavras.

     O compressor existe SÓ no caminho amplificado. Em volume normal nada disso é
     montado, e o áudio segue pelo elemento, sem custo de CPU.
  ========================================================================== */

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const amplificado = volume > 1;

    if (!amplificado && audioGraph.current === null) {
      // Caminho comum: nada a fazer, o `element.volume` já resolveu.
      return;
    }

    if (amplificado && audioGraph.current === null) {
      const Ctor =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext;
      if (Ctor === undefined) return;

      try {
        const context = new Ctor();
        const source = context.createMediaStreamSource(stream);

        const compressor = context.createDynamicsCompressor();
        compressor.threshold.value = -30;
        compressor.ratio.value = 6;
        compressor.knee.value = 12;
        compressor.attack.value = 0.004;
        compressor.release.value = 0.22;

        const gain = context.createGain();

        /*
         * A ordem é compressor ANTES do ganho, e ela não é intercambiável.
         *
         * Comprimir depois de amplificar significa deixar o sinal estourar
         * primeiro e tentar consertar o que já foi serrado — e informação
         * serrada não volta. Comprimindo antes, o ganho recebe um sinal com a
         * faixa já controlada e só o levanta.
         */
        source.connect(compressor).connect(gain).connect(context.destination);
        audioGraph.current = { context, gain };
      } catch {
        /*
         * Falhar aqui é aceitável e não é motivo de aviso.
         *
         * A causa realista é uma faixa sem áudio ou um contexto recusado por
         * política. O som continua saindo pelo elemento, no volume máximo dele —
         * ou seja, degrada para 100%, que é onde estávamos antes.
         */
        audioGraph.current = null;
        return;
      }
    }

    const graph = audioGraph.current;
    if (graph === null) return;

    /*
     * `AudioContext` nasce suspenso quando não houve gesto do usuário. É a mesma
     * política do BUG 3 acima, por outra porta — e aqui SEMPRE houve gesto,
     * porque mexer no controle de volume é o gesto.
     */
    if (graph.context.state === 'suspended') void graph.context.resume();

    graph.gain.gain.value = amplificado ? Math.min(3, volume) : 0;
    // Enquanto o grafo amplifica, o elemento cala — senão soma com ele.
    element.muted = volume === 0 || amplificado;
  }, [volume, stream]);

  useEffect(
    () => () => {
      void audioGraph.current?.context.close().catch(() => undefined);
      audioGraph.current = null;
    },
    [],
  );

  useEffect(() => {
    const element = ref.current;
    if (!element || outputDeviceId === null) return;

    /*
     * `setSinkId` só existe no Chromium, e a ausência não é erro.
     *
     * Onde não existe, o áudio vai para a saída padrão do sistema — que é o
     * comportamento razoável. Lançar aqui impediria a chamada de funcionar no
     * Firefox por causa de uma preferência de dispositivo.
     */
    if (!('setSinkId' in element)) return;
    void (element as HTMLAudioElement & { setSinkId: (id: string) => Promise<void> })
      .setSinkId(outputDeviceId)
      .catch(() => undefined);
  }, [outputDeviceId]);

  return <audio ref={ref} autoPlay playsInline />;
}
